import { Controller } from "@hotwired/stimulus";
import { paintRangeFill } from "../lib/range_fill";

/**
 * Une valeur bornée, dans un champ qui se tape ou se glisse (le « scrub » du panneau Apparence de la démo) :
 *
 *   <label data-controller="range-field" data-range-field-min-value="0" data-range-field-max-value="100"
 *          data-action="pointerdown->range-field#scrub">
 *          data-action="pointerdown->range-field#scrub dblclick->range-field#type">
 *     <input type="text" inputmode="decimal" data-range-field-target="input"
 *            data-action="focus->range-field#edit keydown->range-field#key blur->range-field#commit
 *                         range-field:input->host#live range-field:change->host#done">
 *     <span>%</span>
 *   </label>
 *
 * Tout le champ se glisse : appui puis déplacement horizontal, un pas tous les 2 px, ×10 avec
 * ⇧ ; le geste ne démarre qu'après 3 px (un tremblement ne change rien), et un simple clic ne
 * fait rien. Taper : un double-clic (ou Tab) met le texte en édition et le sélectionne ; ↵ ou
 * la sortie valide, Échap annule, ↑ ↓ font ±1 pas (⇧ ±10). La valeur est toujours bornée et
 * arrondie au pas.
 *
 * L'hôte écoute deux événements sur l'input, jamais le `input` natif (il porterait la frappe
 * en cours) : `range-field:input` à chaque mouvement, `range-field:change` à chaque valeur
 * posée. Pendant le geste, la racine porte `data-scrubbing` — le composant la stylise.
 *
 * Un curseur (`range`, optionnel — un `Ui::RangeComponent`) peut l'accompagner : il règle la
 * même valeur, et sa piste se remplit jusqu'à elle.
 */
export default class extends Controller {
  static targets = ["input", "range"];

  static values = {
    min: { type: Number, default: -Infinity },
    max: { type: Number, default: Infinity },
    step: { type: Number, default: 1 },
  };

  connect() {
    this.fit();
  }

  rangeTargetConnected() {
    this.syncRange();
  }

  get value() {
    return Number(this.inputTarget.value) || 0;
  }

  // ==================== Glisser ====================

  scrub(e) {
    if (e.button !== 0 || document.activeElement === this.inputTarget) return;
    e.preventDefault();
    const x0 = e.clientX;
    const v0 = this.value;
    let started = false;

    const move = (ev) => {
      const dx = ev.clientX - x0;
      if (!started && Math.abs(dx) < 3) return;
      if (!started) {
        started = true;
        this.element.dataset.scrubbing = "";
        document.body.style.cursor = "ew-resize";
      }
      this.set(v0 + Math.round(dx / 2) * this.stepValue * (ev.shiftKey ? 10 : 1), { live: true });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      document.body.style.cursor = "";
      delete this.element.dataset.scrubbing;
      if (started) this.emit("change");
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }

  // ==================== Le curseur ====================

  slide() {
    this.set(Number(this.rangeTarget.value), { live: true });
  }

  slideEnd() {
    this.emit("change");
  }

  // ==================== Taper ====================

  /** Le double-clic : le texte passe en édition (le focus fait le reste). */
  type() {
    this.inputTarget.focus();
    this.inputTarget.select();
  }

  edit() {
    this.before = this.inputTarget.value;
    this.inputTarget.select();
  }

  key(e) {
    if (e.key === "Enter") {
      this.commit();
      this.inputTarget.blur();
    } else if (e.key === "Escape") {
      this.inputTarget.value = this.before ?? this.inputTarget.value;
      this.fit();
      this.inputTarget.blur();
    } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      this.set(this.value + (e.key === "ArrowUp" ? 1 : -1) * this.stepValue * (e.shiftKey ? 10 : 1));
    }
  }

  /** Ce qui est tapé devient la valeur — bornée, arrondie — si c'est un nombre. */
  commit() {
    const typed = parseFloat(this.inputTarget.value.replace(",", "."));
    if (Number.isNaN(typed)) {
      this.inputTarget.value = this.before ?? "";
      return this.fit();
    }
    this.set(typed, { silent: this.clamp(typed) === Number(this.before) });
  }

  // ==================== État ====================

  clamp(v) {
    const stepped = Math.round(v / this.stepValue) * this.stepValue;
    return Number(Math.max(this.minValue, Math.min(this.maxValue, stepped)).toFixed(4));
  }

  set(v, { live = false, silent = false } = {}) {
    this.inputTarget.value = String(this.clamp(v));
    this.before = this.inputTarget.value;
    this.fit();
    this.syncRange();
    if (silent) return;
    this.emit("input");
    if (!live) this.emit("change");
  }

  emit(name) {
    this.inputTarget.dispatchEvent(new CustomEvent(`range-field:${name}`, { bubbles: true }));
  }

  /** Le curseur suit la valeur ; sa piste se remplit jusqu'à elle. */
  syncRange() {
    if (!this.hasRangeTarget) return;
    const range = this.rangeTarget;
    range.value = this.inputTarget.value;
    paintRangeFill(range);
  }

  /** Le champ a la largeur de sa valeur (l'unité le suit à un pixel). */
  fit() {
    this.inputTarget.size = Math.max(1, this.inputTarget.value.length);
  }
}
